import React, { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { toast } from 'react-toastify';
import { Campaign } from '../../../../types/campaign';
import { updateCampaign } from '../../../../api/campaignApi';
import { BlockedKeywords } from '../../../../types/blocked-keywords';
import { getBlockedKeywords } from '../../../../api/blockedKeywordsApi';
import { Loader2 } from 'lucide-react';

interface EditCampaignModalProps {
    campaign: Campaign;
    isOpen: boolean;
    toggle: () => void;
    onUpdateData: (newData: Campaign) => void;
}

const EditCampaignModal = ({ campaign, isOpen, toggle, onUpdateData }: EditCampaignModalProps) => {
    const [loading, setLoading] = useState<boolean>(false);
    const [palavrasChaveProtegida, setPalavrasChaveProtegida] = useState<BlockedKeywords[]>([]);

    useEffect(() => {
        getBlockedKeywords().then(setPalavrasChaveProtegida);
    }, []);

    const toggleModal = () => {
        toggle();
    };

    const handleForm: any = useFormik({
        // enableReinitialize : use this flag when initial values needs to be changed
        enableReinitialize: true,

        initialValues: {
            id: campaign.id,
            name: campaign.name,
            blockedKeywords: campaign.blockedKeywords.map(p => p.id),
        },
        validationSchema: Yup.object({
            name: Yup.string()
                .trim()
                .required('Nome é obrigatório'),
            blockedKeywords: Yup.array(),
        }),
        onSubmit: (values: any) => {
            setLoading(true);
            updateCampaign(values)
                .then((res: any) => {
                    toggleModal();
                    onUpdateData(res);
                    toast.success('Campanha atualizada com sucesso!');
                })
                .catch(() => {
                })
                .finally(() => {
                    setLoading(false);
                });
        },
    });

    return (
        <Dialog open={isOpen} onOpenChange={toggleModal}>
            <DialogContent className="sm:max-w-[425px]">
                <DialogHeader>
                    <DialogTitle>Editar Campanha</DialogTitle>
                    <DialogDescription>
                        Edite as informações da campanha.
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleForm.handleSubmit();
                    return false;
                }}>
                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nome</Label>
                            <Input
                                type="text"
                                id="name"
                                name="name"
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                value={handleForm.values.name}
                            />
                            {handleForm.touched.name && handleForm.errors.name && (
                                <p className="text-sm text-red-500">{handleForm.errors.name}</p>
                            )}
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="palavras-chave-protegidas-field">Proteções: Lista de palavras</Label>
                            <Input
                                type="select"
                                multiple
                                id="palavras-chave-protegidas-field"
                                name="blockedKeywords"
                                onChange={handleForm.handleChange}
                                onBlur={handleForm.handleBlur}
                                value={handleForm.values.blockedKeywords}
                            >
                                {palavrasChaveProtegida.map((palavraChave) => (
                                    <option
                                        key={palavraChave.id}
                                        value={palavraChave.id}
                                    >
                                        {palavraChave.name}
                                    </option>
                                ))}
                            </Input>
                            {handleForm.touched.blockedKeywords && handleForm.errors.blockedKeywords && (
                                <p className="text-sm text-red-500">{handleForm.errors.blockedKeywords}</p>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" disabled={loading}>
                            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Atualizar
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
};

export default EditCampaignModal;